import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  ArrowDownLeft,
  ArrowUpRight,
  DollarSign,
  TrendingUp,
  Plus,
  Search,
  CreditCard,
  Building,
  User,
  Calendar,
  Hash,
  FileText,
  Download,
  FileSpreadsheet,
  Receipt
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = [
  { value: 'check', label: 'Check' },
  { value: 'ach', label: 'ACH Transfer' },
  { value: 'wire', label: 'Wire Transfer' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'stripe', label: 'Stripe' },
  { value: 'other', label: 'Other' },
];

const Payments = () => {
  const { getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('received');
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRecordDialog, setShowRecordDialog] = useState(false);
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payables, setPayables] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [formData, setFormData] = useState({
    payment_type: 'received',
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'check',
    reference_number: '',
    client_id: '',
    vendor_id: '',
    invoice_ids: [],
    payable_ids: [],
    notes: ''
  });

  useEffect(() => {
    fetchPayments();
    fetchSummary();
    fetchClients();
    fetchVendors();
  }, []);

  useEffect(() => {
    fetchPayments();
  }, [activeTab]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        payment_type: activeTab,
        limit: '100'
      });
      
      const response = await fetch(`${API_URL}/api/payments?${params}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPayments(data.payments);
      }
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/summary`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        setSummary(await response.json());
      }
    } catch (error) {
      console.error('Failed to load summary');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/clients?limit=200`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        setClients(await response.json());
      }
    } catch (error) {
      console.error('Failed to load clients');
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await fetch(`${API_URL}/api/vendors?limit=200`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        setVendors(await response.json());
      }
    } catch (error) {
      console.error('Failed to load vendors');
    }
  };

  const fetchUnpaidInvoices = async (clientId) => {
    try {
      const response = await fetch(`${API_URL}/api/invoices?client_id=${clientId}&status=sent`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices || data);
      }
    } catch (error) {
      console.error('Failed to load invoices');
    }
  };

  const fetchUnpaidPayables = async (vendorId) => {
    try {
      const response = await fetch(`${API_URL}/api/payables?vendor_id=${vendorId}&status=approved`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPayables(data.payables || data);
      }
    } catch (error) {
      console.error('Failed to load payables');
    }
  };

  const handleClientChange = (clientId) => {
    setFormData({ ...formData, client_id: clientId, invoice_ids: [] });
    if (clientId) {
      fetchUnpaidInvoices(clientId);
    } else {
      setInvoices([]);
    }
  };

  const handleVendorChange = (vendorId) => {
    setFormData({ ...formData, vendor_id: vendorId, payable_ids: [] });
    if (vendorId) {
      fetchUnpaidPayables(vendorId);
    } else {
      setPayables([]);
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          amount: parseFloat(formData.amount)
        })
      });
      
      if (response.ok) {
        toast.success('Payment recorded successfully');
        setShowRecordDialog(false);
        resetForm();
        fetchPayments();
        fetchSummary();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to record payment');
      }
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const resetForm = () => {
    setFormData({
      payment_type: activeTab,
      amount: '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'check',
      reference_number: '',
      client_id: '',
      vendor_id: '',
      invoice_ids: [],
      payable_ids: [],
      notes: ''
    });
    setInvoices([]);
    setPayables([]);
  };

  const openRecordDialog = (type) => {
    setFormData({ ...formData, payment_type: type });
    setShowRecordDialog(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getMethodLabel = (method) => {
    return PAYMENT_METHODS.find(m => m.value === method)?.label || method;
  };

  const handleExportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (activeTab !== 'all') params.append('payment_type', activeTab);
      
      const response = await fetch(`${API_URL}/api/payments/export/csv?${params}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `payments_${activeTab}_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('CSV exported successfully');
      } else {
        toast.error('Failed to export CSV');
      }
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const handleDownloadReceipt = async (paymentId) => {
    try {
      const response = await fetch(`${API_URL}/api/payments/${paymentId}/receipt/pdf`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `receipt_${paymentId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Receipt downloaded');
      } else {
        toast.error('Failed to download receipt');
      }
    } catch (error) {
      toast.error('Failed to download receipt');
    }
  };

  const filteredPayments = payments.filter(p => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      p.client_name?.toLowerCase().includes(query) ||
      p.vendor_name?.toLowerCase().includes(query) ||
      p.reference_number?.toLowerCase().includes(query) ||
      p.payment_id?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="space-y-6" data-testid="payments-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign className="h-6 w-6 text-green-600" />
            Payments
          </h1>
          <p className="text-muted-foreground">Track payments received and made</p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleExportCsv}
            data-testid="export-csv-btn"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={() => openRecordDialog('received')} className="bg-green-600 hover:bg-green-700">
            <ArrowDownLeft className="h-4 w-4 mr-2" />
            Record Payment Received
          </Button>
          <Button onClick={() => openRecordDialog('made')} variant="outline">
            <ArrowUpRight className="h-4 w-4 mr-2" />
            Record Payment Made
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Received</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(summary?.received?.total)}
                </p>
                <p className="text-xs text-muted-foreground">{summary?.received?.count || 0} payments</p>
              </div>
              <ArrowDownLeft className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Paid Out</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(summary?.made?.total)}
                </p>
                <p className="text-xs text-muted-foreground">{summary?.made?.count || 0} payments</p>
              </div>
              <ArrowUpRight className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Net Cash Flow</p>
                <p className={`text-2xl font-bold ${(summary?.net_cash_flow || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(summary?.net_cash_flow)}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Transactions</p>
                <p className="text-2xl font-bold text-purple-600">
                  {(summary?.received?.count || 0) + (summary?.made?.count || 0)}
                </p>
              </div>
              <CreditCard className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payments Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="received" className="flex items-center gap-2">
                  <ArrowDownLeft className="h-4 w-4" />
                  Received
                </TabsTrigger>
                <TabsTrigger value="made" className="flex items-center gap-2">
                  <ArrowUpRight className="h-4 w-4" />
                  Made
                </TabsTrigger>
              </TabsList>
            </Tabs>
            
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search payments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <DollarSign className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No payments recorded yet</p>
              <Button 
                variant="link" 
                onClick={() => openRecordDialog(activeTab)}
                className="mt-2"
              >
                Record your first payment
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>{activeTab === 'received' ? 'Client' : 'Vendor'}</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPayments.map((payment) => (
                  <TableRow key={payment.payment_id} data-testid={`payment-row-${payment.payment_id}`}>
                    <TableCell>{formatDate(payment.payment_date)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {activeTab === 'received' ? (
                          <Building className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <User className="h-4 w-4 text-muted-foreground" />
                        )}
                        {payment.client_name || payment.vendor_name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{getMethodLabel(payment.payment_method)}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {payment.reference_number || '-'}
                    </TableCell>
                    <TableCell className={`text-right font-semibold ${activeTab === 'received' ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(payment.amount)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownloadReceipt(payment.payment_id)}
                        data-testid={`download-receipt-${payment.payment_id}`}
                      >
                        <Receipt className="h-4 w-4 mr-1" />
                        Receipt
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Record Payment Dialog */}
      <Dialog open={showRecordDialog} onOpenChange={setShowRecordDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {formData.payment_type === 'received' ? (
                <>
                  <ArrowDownLeft className="h-5 w-5 text-green-600" />
                  Record Payment Received
                </>
              ) : (
                <>
                  <ArrowUpRight className="h-5 w-5 text-red-600" />
                  Record Payment Made
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Client/Vendor Selection */}
            {formData.payment_type === 'received' ? (
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select value={formData.client_id} onValueChange={handleClientChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.client_id} value={client.client_id}>
                        {client.client_number && `${client.client_number} - `}{client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Vendor *</Label>
                <Select value={formData.vendor_id} onValueChange={handleVendorChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map((vendor) => (
                      <SelectItem key={vendor.vendor_id} value={vendor.vendor_id}>
                        {vendor.vendor_number && `${vendor.vendor_number} - `}{vendor.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Amount and Date */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="pl-7"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Payment Date *</Label>
                <Input
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                />
              </div>
            </div>

            {/* Payment Method and Reference */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payment Method *</Label>
                <Select 
                  value={formData.payment_method} 
                  onValueChange={(v) => setFormData({ ...formData, payment_method: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((method) => (
                      <SelectItem key={method.value} value={method.value}>
                        {method.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reference # (Check #, Trans ID)</Label>
                <Input
                  value={formData.reference_number}
                  onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                  placeholder="e.g., 1234"
                />
              </div>
            </div>

            {/* Apply to Invoices/Payables */}
            {formData.payment_type === 'received' && invoices.length > 0 && (
              <div className="space-y-2">
                <Label>Apply to Invoices (optional)</Label>
                <div className="border rounded-lg max-h-32 overflow-y-auto p-2 space-y-1">
                  {invoices.map((inv) => (
                    <label key={inv.invoice_id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted p-1 rounded">
                      <input
                        type="checkbox"
                        checked={formData.invoice_ids.includes(inv.invoice_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, invoice_ids: [...formData.invoice_ids, inv.invoice_id] });
                          } else {
                            setFormData({ ...formData, invoice_ids: formData.invoice_ids.filter(id => id !== inv.invoice_id) });
                          }
                        }}
                      />
                      <span>{inv.invoice_number}</span>
                      <span className="text-muted-foreground">- {formatCurrency(inv.total)}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {formData.payment_type === 'made' && payables.length > 0 && (
              <div className="space-y-2">
                <Label>Apply to Payables (optional)</Label>
                <div className="border rounded-lg max-h-32 overflow-y-auto p-2 space-y-1">
                  {payables.map((pay) => (
                    <label key={pay.payable_id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted p-1 rounded">
                      <input
                        type="checkbox"
                        checked={formData.payable_ids.includes(pay.payable_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, payable_ids: [...formData.payable_ids, pay.payable_id] });
                          } else {
                            setFormData({ ...formData, payable_ids: formData.payable_ids.filter(id => id !== pay.payable_id) });
                          }
                        }}
                      />
                      <span>{pay.work_order_id}</span>
                      <span className="text-muted-foreground">- {formatCurrency(pay.total)}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Optional notes..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRecordDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={!formData.amount || (formData.payment_type === 'received' ? !formData.client_id : !formData.vendor_id)}
              className={formData.payment_type === 'received' ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payments;
